// /care-plan — weekly summary from Siggy. Adherence, symptom
// highlights, conversation count, plus a Gemini-written 3-sentence
// recap in Siggy's voice.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChatMarkdown } from '../components/ChatMarkdown';
import { SafetyRing } from '../components/SafetyRing';
import { SiggyAvatar } from '../components/SiggyAvatar';
import { SeniorBanner } from '../components/senior/SeniorBanner';
import { SeniorButton } from '../components/senior/SeniorButton';
import { type AuthedFetch, useAuthedFetch } from '../hooks/useAuthedFetch';
import { useDashboard } from '../hooks/useDashboard';
import { useSubjectName } from '../hooks/useSubjectName';
import { AppPage } from '../layouts/AppPage';

interface SymptomHighlight {
  kind: string;
  avgIntensity: number;
  worstIntensity: number;
  days: number;
}

interface CarePlan {
  weekStart: string;
  weekEnd: string;
  adherenceTakenDays: number;
  adherenceSkippedDays: number;
  symptomHighlights: SymptomHighlight[];
  conversationsThisWeek: number;
  newFlagsThisWeek: number;
  siggySummary: string;
}

const SYMPTOM_LABEL: Record<string, string> = {
  energy: 'Energy',
  sleep: 'Sleep',
  pain: 'Pain',
  tinnitus: 'Tinnitus / hearing',
  mood: 'Mood',
  dizziness: 'Dizziness',
  nausea: 'Nausea',
  appetite: 'Appetite',
  cognition: 'Memory or focus',
};

interface FirstWeekStep {
  label: string;
  done: boolean;
  to?: string;
}

function FirstWeekChecklist({ steps }: { steps: FirstWeekStep[] }): JSX.Element {
  return (
    <section className="account-section">
      <h3 className="account-section__title">Week 1 with MedReview</h3>
      <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
        Once you start using these, Siggy will replace this checklist with a real summary of how
        your week went.
      </p>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}>
        {steps.map((s) => (
          <li
            key={s.label}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: 12,
              border: '1.5px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              background: s.done
                ? 'var(--color-surface-secondary)'
                : 'var(--color-surface-primary)',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                border: '2px solid var(--color-border)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                fontWeight: 700,
                background: s.done ? 'var(--color-accent)' : 'transparent',
                color: s.done ? '#fff' : 'var(--color-text-tertiary)',
              }}
            >
              {s.done ? '✓' : ''}
            </span>
            <span
              style={{
                flex: 1,
                fontSize: 16,
                textDecoration: s.done ? 'line-through' : 'none',
                color: s.done ? 'var(--color-text-secondary)' : 'var(--color-text-primary)',
              }}
            >
              {s.label}
            </span>
            {s.to && !s.done ? (
              <Link to={s.to} className="account-link" style={{ fontWeight: 600 }}>
                Start
              </Link>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function CarePlan(): JSX.Element {
  const authedFetch = useAuthedFetch();
  const subject = useSubjectName();
  const [plan, setPlan] = useState<CarePlan | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch('/api/care-plan/this-week');
        const json = (await res.json()) as { ok: boolean; data?: CarePlan };
        if (cancelled) return;
        if (json.ok && json.data) setPlan(json.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedFetch]);

  // Adherence score 0-100 = takenDays / 7 * 100, capped.
  const adherenceScore = plan ? Math.round(Math.min(100, (plan.adherenceTakenDays / 7) * 100)) : 0;
  const { dashboard } = useDashboard();

  // First-week empty state: brand-new user with no logged adherence, no
  // chats, no flags. The old "0 of 7 days, 0 chats, 0 flags" wall read
  // as failure on day one (focus-group round 2 #3, 7/16 personas). Show
  // a checklist instead so the page has direction.
  const isFirstWeek =
    plan &&
    plan.adherenceTakenDays === 0 &&
    plan.adherenceSkippedDays === 0 &&
    plan.conversationsThisWeek === 0 &&
    plan.newFlagsThisWeek === 0;

  const firstWeekSteps: FirstWeekStep[] = [
    {
      label:
        dashboard && dashboard.medicationCount > 0
          ? `Add your medicines (${dashboard.medicationCount} on file).`
          : 'Add your medicines.',
      done: Boolean(dashboard && dashboard.medicationCount > 0),
      to: '/medications/add-by-photo',
    },
    {
      label: 'Mark a dose taken in the Pillbox.',
      done: false,
      to: '/pillbox',
    },
    {
      label: 'Ask Siggy your first question.',
      done: false,
      to: '/chat',
    },
    {
      label: 'Set a reminder time.',
      done: false,
      to: '/reminders',
    },
  ];

  return (
    <AppPage
      title={subject.possessive ? `${subject.possessive} week` : 'Your week'}
      back={{ to: '/', label: 'Home' }}
    >
      <section className="food-intro">
        <SiggyAvatar size={48} />
        <div>
          <p className="food-intro__eyebrow">Your week with Siggy</p>
          <p className="food-intro__text">
            Here is how the past seven days looked, in Siggy's words.
          </p>
        </div>
      </section>

      {loading ? <p style={{ padding: 'var(--space-md)' }}>Loading...</p> : null}

      {isFirstWeek ? <FirstWeekChecklist steps={firstWeekSteps} /> : null}

      {plan && !isFirstWeek ? (
        <>
          <section className="account-section" style={{ display: 'grid', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
              <SafetyRing score={adherenceScore} size={140} label="days with pills taken" />
              <div>
                <p style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
                  {plan.adherenceTakenDays} of 7 days
                </p>
                <p
                  style={{
                    fontSize: 16,
                    color: 'var(--color-text-secondary)',
                    margin: '4px 0 0',
                  }}
                >
                  Marked at least one medicine taken on {plan.adherenceTakenDays} day
                  {plan.adherenceTakenDays === 1 ? '' : 's'} this week.{' '}
                  {plan.adherenceSkippedDays > 0
                    ? `Skipped doses noted on ${plan.adherenceSkippedDays} day${plan.adherenceSkippedDays === 1 ? '' : 's'}.`
                    : ''}
                </p>
              </div>
            </div>
          </section>

          <section className="account-section">
            <h3 className="account-section__title">Siggy's note</h3>
            <ChatMarkdown text={plan.siggySummary} />
          </section>

          {plan.symptomHighlights.length > 0 ? (
            <section className="account-section">
              <h3 className="account-section__title">Symptom highlights</h3>
              <ul className="food-list">
                {plan.symptomHighlights.map((s) => (
                  <li key={s.kind} className="food-item">
                    <span
                      className={`food-pill ${s.worstIntensity >= 2 ? 'food-pill--limit' : 'food-pill--note'}`}
                    >
                      {s.worstIntensity >= 2 ? 'Watch' : 'Mild'}
                    </span>
                    <div>
                      <p className="food-item__name">{SYMPTOM_LABEL[s.kind] ?? s.kind}</p>
                      <p className="food-item__reason">
                        Average {s.avgIntensity.toFixed(1)} of 3 across {s.days} day
                        {s.days === 1 ? '' : 's'}.
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <section className="account-section">
            <h3 className="account-section__title">Activity</h3>
            <div className="legend-row">
              <span>
                <strong>{plan.conversationsThisWeek}</strong> chat
                {plan.conversationsThisWeek === 1 ? '' : 's'} this week
              </span>
              <span>·</span>
              <span>
                <strong>{plan.newFlagsThisWeek}</strong> safety scan
                {plan.newFlagsThisWeek === 1 ? '' : 's'}
              </span>
              <span>·</span>
              <span>
                Week of {plan.weekStart} to {plan.weekEnd}
              </span>
            </div>
          </section>
        </>
      ) : null}

      {plan ? <AppointmentsSection authedFetch={authedFetch} /> : null}
    </AppPage>
  );
}

// ---------------------------------------------------------------------------
// Appointments (Marlee UAT 2026-05-20)
// ---------------------------------------------------------------------------

interface DoctorOption {
  id: string;
  name: string;
  specialty: string | null;
}

interface Appointment {
  id: string;
  doctorId: string | null;
  doctorName: string | null;
  doctorSpecialty: string | null;
  title: string;
  location: string | null;
  scheduledAt: string;
  notes: string | null;
}

interface DraftAppointment {
  title: string;
  doctorId: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:MM
  location: string;
  notes: string;
}

function emptyDraft(): DraftAppointment {
  const now = new Date();
  // Default to next-week 10am so the date picker is "in the future."
  const next = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const yyyy = next.getFullYear();
  const mm = String(next.getMonth() + 1).padStart(2, '0');
  const dd = String(next.getDate()).padStart(2, '0');
  return {
    title: '',
    doctorId: '',
    date: `${yyyy}-${mm}-${dd}`,
    time: '10:00',
    location: '',
    notes: '',
  };
}

function AppointmentsSection({ authedFetch }: { authedFetch: AuthedFetch }): JSX.Element {
  const [appointments, setAppointments] = useState<Appointment[] | null>(null);
  const [doctors, setDoctors] = useState<DoctorOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<DraftAppointment>(emptyDraft());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [aRes, dRes] = await Promise.all([
          authedFetch('/api/appointments?scope=upcoming'),
          authedFetch('/api/doctors'),
        ]);
        if (cancelled) return;
        const aJson = (await aRes.json()) as { ok: boolean; data?: { items: Appointment[] } };
        const dJson = (await dRes.json()) as { ok: boolean; data?: { items: DoctorOption[] } };
        if (aJson.ok && aJson.data) setAppointments(aJson.data.items);
        if (dJson.ok && dJson.data) setDoctors(dJson.data.items);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedFetch]);

  const sortedAppointments = useMemo(() => appointments ?? [], [appointments]);

  async function save() {
    if (!draft.title.trim()) {
      setError('Appointment title is required.');
      return;
    }
    if (!draft.date) {
      setError('Pick a date.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Compose ISO timestamp in the user's local timezone, then let
      // the browser convert to UTC for the API. Backend stores
      // TIMESTAMPTZ so wall-clock is preserved on the way back.
      const localIso = new Date(`${draft.date}T${draft.time || '10:00'}:00`).toISOString();
      const res = await authedFetch('/api/appointments', {
        method: 'POST',
        body: JSON.stringify({
          title: draft.title.trim(),
          doctorId: draft.doctorId || null,
          location: draft.location.trim() || null,
          scheduledAt: localIso,
          notes: draft.notes.trim() || null,
        }),
      });
      const body = (await res.json()) as {
        ok: boolean;
        data?: Appointment;
        error?: { message?: string };
      };
      if (!body.ok || !body.data) throw new Error(body.error?.message ?? `HTTP ${res.status}`);
      setAppointments((prev) => {
        const next = [...(prev ?? []), body.data as Appointment];
        next.sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
        return next;
      });
      setDraft(emptyDraft());
      setAdding(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    if (!window.confirm('Remove this appointment?')) return;
    setError(null);
    try {
      const res = await authedFetch(`/api/appointments/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setAppointments((prev) => (prev ? prev.filter((a) => a.id !== id) : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <section className="account-section">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 12,
        }}
      >
        <h3 className="account-section__title" style={{ margin: 0 }}>
          Upcoming appointments
        </h3>
        {!adding ? <SeniorButton onClick={() => setAdding(true)}>Add</SeniorButton> : null}
      </div>

      {error ? (
        <SeniorBanner title="Could not update appointments" tone="critical">
          {error}
        </SeniorBanner>
      ) : null}

      {appointments === null ? (
        <p style={{ fontSize: 15, color: 'var(--color-text-secondary)', margin: 0 }}>Loading...</p>
      ) : sortedAppointments.length === 0 && !adding ? (
        <p style={{ fontSize: 15, color: 'var(--color-text-secondary)', margin: 0 }}>
          No upcoming appointments. Tap <strong>Add</strong> to save your next visit so it shows on
          this page and the printable visit summary.
        </p>
      ) : null}

      {sortedAppointments.length > 0 ? (
        <ul className="food-list" style={{ marginTop: 8 }}>
          {sortedAppointments.map((a) => (
            <li key={a.id} className="food-item" style={{ alignItems: 'flex-start' }}>
              <span
                className="food-pill food-pill--note"
                style={{ minWidth: 88, textAlign: 'center' }}
              >
                {formatDate(a.scheduledAt)}
              </span>
              <div style={{ flex: 1 }}>
                <p className="food-item__name">{a.title}</p>
                <p className="food-item__reason">
                  {formatTime(a.scheduledAt)}
                  {a.doctorName ? ` · ${a.doctorName}` : ''}
                  {a.doctorSpecialty ? ` (${a.doctorSpecialty})` : ''}
                  {a.location ? ` · ${a.location}` : ''}
                </p>
                {a.notes ? (
                  <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                    {a.notes}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => void remove(a.id)}
                style={{
                  background: 'transparent',
                  border: 0,
                  color: 'var(--color-text-secondary)',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
                aria-label={`Remove ${a.title}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {adding ? (
        <div
          style={{
            marginTop: 12,
            padding: 'var(--space-md)',
            border: '1px solid var(--color-border-subtle)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-secondary)',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>What is the appointment for?</span>
            <input
              type="text"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="Annual physical, cardiology follow-up..."
              style={inputStyle}
            />
          </label>
          {doctors.length > 0 ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 16, fontWeight: 600 }}>With which doctor?</span>
              <select
                value={draft.doctorId}
                onChange={(e) => setDraft({ ...draft, doctorId: e.target.value })}
                style={inputStyle}
              >
                <option value="">(not in my doctor list)</option>
                {doctors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                    {d.specialty ? ` · ${d.specialty}` : ''}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 16, fontWeight: 600 }}>Date</span>
              <input
                type="date"
                value={draft.date}
                onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                style={inputStyle}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 16, fontWeight: 600 }}>Time</span>
              <input
                type="time"
                value={draft.time}
                onChange={(e) => setDraft({ ...draft, time: e.target.value })}
                style={inputStyle}
              />
            </label>
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>Location (optional)</span>
            <input
              type="text"
              value={draft.location}
              onChange={(e) => setDraft({ ...draft, location: e.target.value })}
              placeholder="Address or office name"
              style={inputStyle}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>Notes (optional)</span>
            <textarea
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              rows={2}
              placeholder="Bring bottle of lisinopril, fasting required..."
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </label>
          <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
            <SeniorButton variant="primary" onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving...' : 'Save appointment'}
            </SeniorButton>
            <SeniorButton
              onClick={() => {
                setAdding(false);
                setDraft(emptyDraft());
                setError(null);
              }}
              disabled={saving}
            >
              Cancel
            </SeniorButton>
          </div>
        </div>
      ) : null}
    </section>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '12px 14px',
  fontSize: 18,
  minHeight: 48,
  border: '1.5px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-primary)',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}
